import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { SeoService } from '@shared';

export interface RouteSeoData {
  titleKey?: string;
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
}

function resolveSeoTitle(translate: TranslateService, seo: RouteSeoData): string | undefined {
  let title: string | undefined;
  if (seo.titleKey) {
    const translated = translate.instant(seo.titleKey);
    if (translated && translated !== seo.titleKey) {
      title = translated;
    }
  }
  if (!title) {
    title = seo.title;
  }
  if (title) {
    title = title.replace(/\s*\|\s*CartCo\s*$/i, '').trim();
  }
  return title;
}

export function applyRouteSeo(
  seoService: SeoService,
  translate: TranslateService,
  seo: RouteSeoData,
): void {
  seoService.setTags({
    title: resolveSeoTitle(translate, seo),
    description: seo.description,
    keywords: seo.keywords,
    image: seo.image,
    url: seo.url,
    type: seo.type || 'website',
  });
}

function findDeepestSeoData(route: ActivatedRouteSnapshot): RouteSeoData | null {
  let current: ActivatedRouteSnapshot | null = route;
  let found: RouteSeoData | null = null;
  while (current) {
    const seo = current.data['seo'] as RouteSeoData | undefined;
    if (seo) {
      found = seo;
    }
    current = current.firstChild;
  }
  return found;
}

/**
 * Route-based SEO title resolver.
 * Translates `titleKey` when present; SeoService always appends English "CartCo".
 */
export const seoTitleResolver: ResolveFn<void> = (route) => {
  const seoService = inject(SeoService);
  const translate = inject(TranslateService);
  const seoData = route.data['seo'] as RouteSeoData | undefined;

  if (seoData) {
    applyRouteSeo(seoService, translate, seoData);
  }
};

/** Re-applies the current route SEO title when the UI language changes. */
@Injectable({ providedIn: 'root' })
export class SeoTitleI18nService {
  constructor() {
    const router = inject(Router);
    const translate = inject(TranslateService);
    const seoService = inject(SeoService);

    translate.onLangChange.subscribe(() => {
      const seo = findDeepestSeoData(router.routerState.snapshot.root);
      if (seo) {
        applyRouteSeo(seoService, translate, seo);
      }
    });
  }
}
